<script context="module" lang="ts">
  import Icon, { type IconName } from "../icons/Icon.svelte";
  export type TransactionStatusKey = `${TransactionType}:${string}`;
  export type TransactionStatusDisplayVariant = {
    iconName: IconName;
    title: string;
  };
  export type SecondaryStatus = "creditApplied" | "resolved" | "unresolved";
</script>

<script lang="ts">
  import type { TransactionType } from "../../api/transactions";
  import TextNew from "../TextNew.svelte";
  import { getTitle } from "../InvoiceStatus.svelte";

  const statusDisplayVariants = {
    paid: {
      iconName: "statusSuccess",
      title: "paid",
    },
    creditApplied: {
      iconName: "statusSuccess",
      title: "credit applied",
    },
    creditInstant: {
      iconName: "statusSuccess",
      title: "REKKI instant credit",
    },
    creditRequested: {
      iconName: "txStatusCreditRequested",
      title: "credit requested",
    },
    creditRejected: {
      iconName: "statusFailure",
      title: "credit rejected",
    },
    orderPlaced: {
      iconName: "txStatusOrdered",
      title: "order placed",
    },
    paidOffRekki: {
      iconName: "txStatusPaidOffRekki",
      title: "paid outside REKKI",
    },
    resolvedIssue: {
      iconName: "statusSuccess",
      title: "resolved",
    },
    unresolvedIssue: {
      iconName: "exclamationError",
      title: "not resolved",
    },
  } as const;

  const secondaryStatusVariants = {
    creditApplied: {
      iconName: "txStatusApplied",
      title: "credit applied",
    },
    resolved: {
      iconName: "statusSuccess",
      title: "resolved",
    },
    unresolved: {
      iconName: "exclamationError",
      title: "issue reported",
    },
  } as const;

  const displayVariantsByStatus: Record<TransactionStatusKey, TransactionStatusDisplayVariant> = {
    "payment:paid": statusDisplayVariants.paid,
    "invoice_arrived:paid": statusDisplayVariants.paid,

    // "payment:in_progress":
    // "payment:refunded":
    // 'payment:partial':
    // 'payment:unpaid':
    // 'payment:not_required':

    // "invoice_arrived:in_progress":
    // "invoice_arrived:refunded":
    // 'invoice_arrived:partial':
    // 'invoice_arrived:unpaid':
    // 'invoice_arrived:not_required':

    // Credit requests
    "credit_request:pending": statusDisplayVariants.creditRequested,
    "credit_request:rejected": statusDisplayVariants.creditRejected,
    "credit_request:creditnote_issued": statusDisplayVariants.creditApplied,
    "credit_request:resolved": statusDisplayVariants.resolvedIssue,

    // Credit/refund rejected
    "refund:rejected": statusDisplayVariants.creditRejected,
    "creditnote:rejected": statusDisplayVariants.creditRejected,

    // Pro instant credit
    "creditnote:order_issue": statusDisplayVariants.creditInstant,
    "creditnote:manual_issue": statusDisplayVariants.creditInstant,
    "creditnote:invoice_issue": statusDisplayVariants.creditInstant,
    "refund:order_issue": statusDisplayVariants.creditInstant,
    "refund:manual_issue": statusDisplayVariants.creditInstant,
    "refund:invoice_issue": statusDisplayVariants.creditInstant,

    // Credit note applied
    "creditnote:creditnote_issued": statusDisplayVariants.creditApplied,
    "creditnote:creditnote": statusDisplayVariants.creditApplied,
    "creditnote:creditnote_diff_amount": statusDisplayVariants.creditApplied,

    // Paid outside REKKI
    "creditnote:double_charge": statusDisplayVariants.paidOffRekki,
    "refund:double_charge": statusDisplayVariants.paidOffRekki,
    "invoice_arrived:non_pro_invoice_added": statusDisplayVariants.paidOffRekki,

    "order:order_new": statusDisplayVariants.orderPlaced,

    // Order Issues
    "checkin:resolved": statusDisplayVariants.resolvedIssue,
    "checkin:unresolved": statusDisplayVariants.unresolvedIssue,
  };

  export let type: TransactionType;
  export let status: string;
  export let secondaryStatus: SecondaryStatus | null = null;
  export let dueDate: string | null;
  export let isUnlinked: boolean;

  $: displayedVariant = displayVariantsByStatus[buildTransactionStatusKey(type, status)];
  $: secondaryStatusVariant = secondaryStatus ? secondaryStatusVariants[secondaryStatus] : null;

  function buildTransactionStatusKey(type: TransactionType, status: string): TransactionStatusKey {
    return `${type}:${status}`;
  }

  function getSecondaryStatusVariant(status: string): { iconName: IconName; title: string } | null {
    switch (status) {
      case "creditApplied":
        return {
          iconName: "txStatusApplied",
          title: "credit applied",
        };
      case "resolved":
        return {
          iconName: "statusSuccess",
          title: "resolved",
        };
      case "unresolved":
        return {
          iconName: "statusFailure",
          title: "unresolved",
        };
      default:
        return null;
    }
  }
</script>

<div class="container">
  <div class="status">
    {#if displayedVariant}
      <Icon name={displayedVariant.iconName} --w="16px" --h="16px" />
    {/if}
    <div>
      <TextNew
        size="xs"
        --rk-text-color={isUnlinked ? "var(--rekki-error)" : "var(--rk-text-dark)"}
      >
        {displayedVariant ? displayedVariant.title : getTitle(status, dueDate)}
      </TextNew>
    </div>
  </div>
  {#if secondaryStatusVariant}
    <div class="status">
      <Icon name={secondaryStatusVariant.iconName} --w="16px" --h="16px" />
      <TextNew size="xs" --rk-text-color="var(--rk-text-dark)">
        {secondaryStatusVariant.title}
      </TextNew>
    </div>
  {/if}
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .status {
    display: flex;
    align-items: center;
    gap: 6px;
  }
</style>
